import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { getCalendarWorkers } from '../../store/selectors';
import InputSelector from '../../../Common/CustomSelector/InputSelector';
import { ReactComponent as Armchair } from '../../../../assets/svg/icons/armchair_blue.svg';
import { ReactComponent as Calendar } from '../../../../assets/svg/icons/calendar_blue.svg';
import CustomDatePicker from '../../../Common/CustomDatePicker';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import styles from './BreakModal.module.scss';
import clx from 'classnames';
import { schedulerActions } from '../../store';
import useWindowSize from '../../../../utils/useWindowSize';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Coffee, Clock3, ArrowRight } from 'lucide-react';
import TimeStepper, {
  atMinutes,
  minutesOf,
  roundToNearestFive,
} from '../../../Common/TimeStepper';
import { getClosureApiFlags } from '../utils/closureHelpers';

const MIN_DURATION_MINUTES = 1;
const DEFAULT_DURATION_MINUTES = 30;

const BreakModal = ({ closeModal }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isMobile } = useWindowSize();
  const [searchParams] = useSearchParams();
  const date = searchParams?.get('startDay') || dayjs().format('YYYY-MM-DD');
  const isCurrent = date === dayjs().format('YYYY-MM-DD');
  const workers = useSelector(getCalendarWorkers());

  const initialStart = roundToNearestFive(isCurrent ? dayjs() : dayjs(date).hour(9).minute(0));
  const initialValues = {
    worker_id: undefined,
    startDate: initialStart,
    endDate: atMinutes(initialStart, minutesOf(initialStart) + DEFAULT_DURATION_MINUTES),
    dateBreak: dayjs(date),
  };

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        worker_id: yup.mixed().required(t('scheduler.breakModal.fields.worker.required')),
        startDate: yup.date().required(t('scheduler.breakModal.fields.startTime.required')),
        endDate: yup.date().required(t('scheduler.breakModal.fields.endTime.required')),
        dateBreak: yup.date().required(t('scheduler.breakModal.fields.date.required')),
      }),
    [t],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        const formedTask = {
          oraInizio: values.startDate.format('HH:mm:ss'),
          oraFine: values.endDate.format('HH:mm:ss'),
          dataPrenotazione: values.dateBreak.format('YYYY-MM-DD'),
          poltrone: [{ idpoltrona: values.worker_id }],
        };
        dispatch(
          schedulerActions.ADD_CALENDAR_BREAK.REQUEST(formedTask, () => {
            dispatch(
              schedulerActions.GET_CALENDAR_DATA.REQUEST({
                startDate: dayjs(date).format('YYYY-MM-DD'),
                endDate: dayjs(date).add(1, 'day').format('YYYY-MM-DD'),
                ...getClosureApiFlags(searchParams),
              }),
            );
            setSubmitting(false);
            closeModal?.();
          }),
        );
        window.setTimeout(() => setSubmitting(false), 6000);
      }}
    >
      {({ values, setFieldValue, errors, touched, setFieldTouched, isSubmitting }) => {
        const durationMinutes = Math.max(
          0,
          minutesOf(values.endDate) - minutesOf(values.startDate),
        );
        const selectedWorker = workers.find((w) => +w.value === +values.worker_id);
        const startTotal = minutesOf(values.startDate);

        const setStartKeepingDuration = (nextStart) => {
          const kept = Math.max(
            MIN_DURATION_MINUTES,
            durationMinutes || DEFAULT_DURATION_MINUTES,
          );
          const nextStartTotal = minutesOf(nextStart);
          setFieldValue('startDate', nextStart);
          setFieldValue('endDate', atMinutes(nextStart, nextStartTotal + kept));
          setFieldTouched('startDate', false);
        };

        return (
          <Form
            className={clx(styles.break_form, {
              [styles.break_form_mobile]: isMobile,
            })}
          >
            <div className={styles.hero}>
              <span className={styles.hero_icon} aria-hidden="true">
                <Coffee size={22} strokeWidth={2.2} />
              </span>
              <div className={styles.hero_copy}>
                <span className={styles.hero_title}>{t('scheduler.breakModal.heroTitle')}</span>
                <span className={styles.hero_subtitle}>
                  {t('scheduler.breakModal.heroSubtitle')}
                </span>
              </div>
            </div>

            <div className={styles.body}>
              <div className={styles.field_block}>
                <InputSelector
                  options={workers}
                  value={values.worker_id}
                  setValue={(id) => {
                    setFieldValue('worker_id', id);
                    setFieldTouched('worker_id', false);
                  }}
                  placeholder={t('scheduler.breakModal.fields.worker.placeholder')}
                  title={t('scheduler.breakModal.fields.worker.title')}
                  icon={<Armchair />}
                  withIcon={true}
                  error={!!touched.worker_id && !!errors.worker_id}
                  isEmptyMessage={t('scheduler.breakModal.fields.worker.empty')}
                />
              </div>

              <div className={styles.field_block}>
                <CustomDatePicker
                  value={values.dateBreak}
                  setValue={(next) => {
                    setFieldValue('dateBreak', next);
                    setFieldTouched('dateBreak', false);
                  }}
                  placeholder={t('scheduler.breakModal.fields.date.title')}
                  title={t('scheduler.breakModal.fields.date.title')}
                  icon={<Calendar />}
                  withIcon={true}
                  minDate={
                    dayjs(date).isBefore(dayjs(), 'day')
                      ? dayjs(date).startOf('day')
                      : dayjs().startOf('day')
                  }
                  error={touched.dateBreak && errors.dateBreak}
                />
              </div>

              <div className={styles.time_row}>
                <div className={styles.time_card}>
                  <TimeStepper
                    label={t('scheduler.breakModal.fields.startTime.title')}
                    value={values.startDate}
                    error={touched.startDate && errors.startDate}
                    hourCaption={t('scheduler.breakModal.fields.hour')}
                    minuteCaption={t('scheduler.breakModal.fields.minute')}
                    onChange={setStartKeepingDuration}
                  />
                </div>
                <div className={styles.time_divider} aria-hidden="true">
                  <span className={styles.time_divider_line} />
                  <span className={styles.time_divider_icon}>
                    <ArrowRight size={16} strokeWidth={2.4} />
                  </span>
                  <span className={styles.time_divider_line} />
                </div>
                <div className={clx(styles.time_card, styles.time_card_end)}>
                  <TimeStepper
                    label={t('scheduler.breakModal.fields.endTime.title')}
                    value={values.endDate}
                    minTotal={startTotal + MIN_DURATION_MINUTES}
                    error={touched.endDate && errors.endDate}
                    hourCaption={t('scheduler.breakModal.fields.hour')}
                    minuteCaption={t('scheduler.breakModal.fields.minute')}
                    onChange={(nextEnd) => {
                      setFieldValue('endDate', nextEnd);
                      setFieldTouched('endDate', false);
                    }}
                  />
                </div>
              </div>

              <div className={styles.summary}>
                <Clock3 size={16} strokeWidth={2.4} aria-hidden="true" />
                <div className={styles.summary_copy}>
                  <span className={styles.summary_title}>
                    {t('scheduler.breakModal.summaryTitle', { minutes: durationMinutes })}
                  </span>
                  <span className={styles.summary_meta}>
                    {selectedWorker?.name
                      ? t('scheduler.breakModal.summaryWithChair', {
                          name: selectedWorker.name,
                          time: `${dayjs(values.startDate).format('HH:mm')} – ${dayjs(
                            values.endDate,
                          ).format('HH:mm')}`,
                          date: dayjs(values.dateBreak).format('DD/MM/YYYY'),
                        })
                      : t('scheduler.breakModal.summaryPickChair')}
                  </span>
                </div>
              </div>
            </div>

            <div className={styles.footer}>
              <button
                type="button"
                className={styles.cancel_button}
                onClick={() => closeModal?.()}
                disabled={isSubmitting}
              >
                {t('common.actions.cancel')}
              </button>
              <button type="submit" className={styles.confirm_button} disabled={isSubmitting}>
                {isSubmitting
                  ? t('scheduler.breakModal.submitting')
                  : t('scheduler.breakModal.submit')}
              </button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default BreakModal;
